/**
 * Follow-ups — the demos the demo team has handed to sales.
 *
 * Nothing arrives here on its own. Somebody on the demo side presses
 * "Follow up" on a booking, writes what happened, and sends it; the row
 * lands here as **Not started**, at the top, waiting for sales to pick it
 * up. Sales starts it, chases it, and closes it with a reason — and a
 * closed one can be reopened, coming back exactly where it left off.
 *
 *   Not started   handed over, nobody has taken it
 *   Following up  sales is chasing it
 *   Closed        finished with, for a reason
 *
 * Opening a row shows the whole record: the brief the demo team wrote, the
 * queries and requirements they took down, everything Calendly's form
 * asked, and the earlier demos this person had. It is the same panel the
 * booking carries, so anything written here shows there too.
 *
 * Times are Dubai's, like every other demo screen.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import PlanUpgradeSheet from '../components/modals/PlanUpgradeSheet.jsx';
import api from '../lib/api.js';
import Icon from '../lib/icons.jsx';
import { Card, CardBody, EmptyState, Avatar, Pill, Spinner } from '../components/Primitives.jsx';
import { BookingDetail, StartWork } from './DemoBookings.jsx';
import { Attendees } from '../components/DemoAssign.jsx';
import { ClientCard, FollowJourney } from '../components/FollowJourney.jsx';
import DealJourney from '../components/DealJourney.jsx';
import { clientOfDeal } from '../domain/crm.js';
import {
  TZ_LABEL, TZ_WORK_LABEL, DAY, dayText, stamp, workStamp, stateOf, followUps, followState, FOLLOW_META,
  STEP_META, CLOSED_META, lastStep, pendingClose, REGISTERED, REG_META, regLabel,
  dubaiDayStart, dubaiToday, ago
} from '../domain/demos.js';

/*
 * The list is cut two ways: where the follow-up has got to, and — once it
 * is being chased or has ended — what the last round came to. A queue has a
 * front, a middle and an end; inside the middle, "which of these went
 * quiet" is the next question anybody asks.
 */
const WHEN = [
  ['handed', 'Not started'],
  ['following', 'Following up'],
  ['closed', 'Closed'],
  ['', '— by outcome —', true],
  ['step:no_response', 'No response'],
  /* The two the team asked for. Filterable like the rest, because "who have
     we only ever mailed" is a question somebody asks on a Monday. */
  ['step:couldnt_connect', "Couldn't connect"],
  ['step:mail_sent', 'Mail sent'],
  ['step:call_back', 'Call back'],
  ['step:follow_up', 'Follow up'],
  ['end:demo_rescheduled', 'Closed · demo rescheduled'],
  ['end:not_interested', 'Closed · not interested'],
  ['end:plan_upgrade', 'Closed · plan upgradation'],
  ['end:registration', 'Closed · registration']
];

/* Which rows to show, by when the demo itself happened. */
const SPANS = [
  ['', 'Any time'],
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['month', 'This month'],
  ['custom', 'Custom range…']
];

const dueAt = s => (s.nextDue ? Date.parse(`${s.nextDue}T23:59:59+04:00`) : null);

/**
 * Everybody inside their trial, soonest to run out at the top.
 *
 * This page was the demos and only the demos, but a demo is not the only
 * thing a follow-up is for: the larger job is the trials, where somebody is
 * already using the product and the fourteen days are running down. Chasing
 * those is what turns one into a paying account, and the day the trial ends
 * is the day the conversation is over.
 *
 * So it is a tab here rather than a fourth screen — the work is the same
 * work, done by the same person in the same sitting, and the Trials menu
 * under Clients is the record while this is the queue.
 *
 * Ordered by what is left, not by name. On this list the row that matters is
 * the one with two days on it, and it is never the one beginning with A.
 */
/**
 * Where a trial has got to, worked out rather than stored.
 *
 * The demo flow keeps its three states on the booking — a demo_sessions row,
 * which is Calendly's record of a meeting. A trial has no meeting and no such
 * row, so there is nowhere to put a fourth state machine without inventing a
 * table for it.
 *
 * It does not need one. "Has anybody picked this up" is already written in
 * two fields the app is allowed to write on a client: who owns it, and when
 * they last spoke to them. Nobody on it is nobody chasing it; an owner and a
 * contact date is somebody chasing it; a trial that has ended has left this
 * list altogether. Deriving it from the two facts means it can never disagree
 * with them, which a stored copy eventually would.
 */
/*
 * How a trial chase can end, in the words the demo follow-up already uses.
 *
 * The two flows are the same conversation with a different starting point —
 * "you had a demo, what now" and "your trial runs out on Thursday, what now"
 * — and the outcomes are identical. Borrowing the vocabulary rather than
 * inventing a second one means a report can count both together.
 *
 * The three marked `end` finish the chase. Plan upgradation is the one this
 * whole screen exists for.
 */
const TRIAL_STEPS = [
  ['no_response', 'No response', 'gr-slate', 'eyeoff'],
  /* Reached out and it failed, as against heard nothing back. The demo
     follow-up offers both and a trial chase is the same conversation, so a
     report can count the two lists together. */
  ['couldnt_connect', "Couldn't connect", 'gr-amber', 'phone'],
  ['mail_sent', 'Mail sent', 'gr-cyan', 'mail'],
  ['call_back', 'Call back', 'gr-blue', 'clock'],
  ['follow_up', 'Follow up', 'gr-violet', 'flag']
];
/*
 * Two ways a trial chase ends, not three.
 *
 * Registration was borrowed from the demo flow, where it means the person
 * signed up — but a trial account has already signed up. That is what a trial
 * is. Offering it here asked somebody to record a thing that had happened
 * before the conversation started.
 */
const TRIAL_ENDS = [
  ['plan_upgrade', 'Plan upgradation', 'gr-green', 'up'],
  ['not_interested', 'Not interested', 'gr-red', 'x']
];
const ALL_OUTCOMES = [...TRIAL_STEPS, ...TRIAL_ENDS];
const OUTCOME = Object.fromEntries(ALL_OUTCOMES.map(([k, ...rest]) => [k, rest]));
const ENDS = new Set(TRIAL_ENDS.map(([k]) => k));

/* A round is an activity against the account, its subject naming the
   outcome. Nothing else in the app writes that subject, so reading it back
   is unambiguous. */
const ROUND = 'Trial follow-up';
const roundsFor = (crm, clientId) => (crm.acts || [])
  .filter(a => a.clientId === clientId && String(a.subject || '').startsWith(ROUND))
  .map(a => ({ ...a, outcome: String(a.subject).slice(ROUND.length + 3).trim() }));

const outcomeKey = label =>
  (ALL_OUTCOMES.find(([, l]) => l === label) || [])[0] || '';

function trialState(r) {
  if (!String(r.handler || '').trim()) return 'handed';
  return r.lastContacted ? 'following' : 'handed';
}

/**
 * Everybody inside their trial, soonest to run out at the top.
 *
 * This page was the demos and only the demos, but a demo is not the only
 * thing a follow-up is for: the larger job is the trials, where somebody is
 * already using the product and the fourteen days are running down. Chasing
 * those is what turns one into a paying account, and the day the trial ends
 * is the day the conversation is over.
 *
 * Ordered by what is left, not by name. On this list the row that matters is
 * the one with two days on it, and it is never the one beginning with A.
 */
function TrialRow({
  r, d, open, onToggle, fmtDate, deal, rounds,
  onStart, onRound, onUpgrade, onFixRound, onDropRound, onLog, onOpen
}) {
  /* Which round is being corrected, and the two fields being corrected. */
  const [editing, setEditing] = useState('');
  const [eKind, setEKind] = useState('follow_up');
  const [eText, setEText] = useState('');
  const left = d.dT;
  const urgent = left !== null && left <= 3;
  const state = trialState(r);
  const [pick, setPick] = useState('call_back');
  const [note, setNote] = useState('');

  /* The last round that ends the chase, if one has been written. */
  const ended = [...rounds].reverse()
    .map(a => outcomeKey(a.outcome)).find(k => ENDS.has(k)) || '';

  return (
    <article className={`futrow${urgent ? ' hot' : ''}${open ? ' open' : ''}`}>
      <div
        className="futhead"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <Avatar name={r.name} />

        <div className="futwho">
          <b>{r.name || 'Untitled'}</b>
          <small>{r.email || r.contact || 'no address on the record'}</small>
        </div>

        <div className="futtags">
          {r.plan ? <Pill tone="gr-violet">{r.plan}</Pill> : null}
          {/* What they came in on. A trial that arrived on a partner's code
              is a different conversation from one that walked in off the
              site, and the code is who to thank for it. */}
          {r.promo ? <Pill tone="gr-amber" icon="tag">{r.promo}</Pill> : null}
          {r.source && r.source !== 'Direct' ? <Pill tone="gr-cyan">{r.source}</Pill> : null}
        </div>

        <div className="futown">
          {r.handler
            ? <Pill tone="gr-slate" icon="users">{r.handler}</Pill>
            : <span>Unowned</span>}
        </div>

        <div className="futleft">
          <b>{left === 0 ? 'today' : left === 1 ? '1 day' : `${left} days`}</b>
          <small>{d.trial ? `ends ${fmtDate(d.trial)}` : 'left'}</small>
        </div>

        <span className={`futstate ${state}`}>
          {state === 'handed' ? 'Not started' : 'Following up'}
        </span>

        {state === 'handed' ? (
          <button
            type="button"
            className="btn sm primary"
            onClick={e => { e.stopPropagation(); onStart(); }}
          >
            <Icon name="flag" />Start follow-up
          </button>
        ) : (
          <button
            type="button"
            className="btn sm"
            onClick={e => { e.stopPropagation(); onToggle(); }}
          >
            <Icon name="chev" />{open ? 'Close' : 'Open'}
          </button>
        )}
      </div>

      {open ? (
        <div className="futopen" onClick={e => e.stopPropagation()}>
          <div className="futfacts">
            <div><dt>Registered</dt><dd>{d.reg ? fmtDate(d.reg) : '—'}</dd></div>
            <div><dt>Trial ends</dt><dd>{d.trial ? fmtDate(d.trial) : '—'}</dd></div>
            <div><dt>Last contacted</dt>
              <dd>{r.lastContacted ? fmtDate(r.lastContacted) : 'never'}</dd></div>
            <div><dt>Promo</dt><dd>{r.promo || '—'}</dd></div>
            <div><dt>Source</dt><dd>{r.source || '—'}</dd></div>
            <div><dt>Owner</dt><dd>{r.handler || 'nobody yet'}</dd></div>
          </div>

          {/*
            * What has actually been said to them.
            *
            * Where the account came in off the pipeline there is a whole
            * trail behind it — the tracker's visits, the calls, the stage
            * moves — and it is the same trail the lead and the deal show. A
            * trial that registered straight off the website has none, and
            * says so rather than drawing an empty spine.
            */}
          {deal ? <DealJourney deal={deal} limit={6} onLog={onLog} /> : null}

          {/*
            * The chase itself: one line per round, oldest first.
            *
            * Each is an activity written against the account — the
            * activities table has carried `client_id` all along and nothing
            * was passing one, which is why a log written here used to vanish.
            */}
          <div className="futrounds">
            {rounds.length ? (
              <ol>
                {rounds.map((a, i) => {
                  const key = outcomeKey(a.outcome);
                  const [label, tone] = OUTCOME[key] || [a.outcome || 'Note', 'gr-slate'];

                  /*
                   * The same correction the demo follow-up carries, on the
                   * same kind of mistake: a round typed into a box under a
                   * button that saves on Enter. The outcome as much as the
                   * note, because picking the wrong one here ends the chase.
                   */
                  if (editing === a.id) {
                    return (
                      <li key={a.id} className="futedit">
                        <span className="futn">{i + 1}</span>
                        <div>
                          <div className="futerow">
                            <select value={eKind} onChange={e => setEKind(e.target.value)} aria-label="Outcome">
                              {ALL_OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                          </div>
                          <input
                            autoFocus
                            value={eText}
                            onChange={e => setEText(e.target.value)}
                            placeholder="What was said"
                            onKeyDown={e => {
                              if (e.key === 'Escape') setEditing('');
                              if (e.key === 'Enter') onFixRound(a, eKind, eText);
                            }}
                          />
                          <div className="futeacts">
                            <button type="button" className="btn sm primary"
                              onClick={() => onFixRound(a, eKind, eText)}>
                              <Icon name="check" />Save
                            </button>
                            <button type="button" className="btn sm" onClick={() => setEditing('')}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  }

                  return (
                    <li key={a.id}>
                      <span className="futn">{i + 1}</span>
                      <Pill tone={tone}>{label}</Pill>
                      <small>{a.due ? fmtDate(a.due) : ''}{a.who ? ` · ${a.who}` : ''}</small>
                      {/* Quiet until the row is hovered: a pair of buttons on
                          every line invites the mis-click they exist to undo. */}
                      <span className="futracts">
                        <button
                          type="button"
                          title="Correct this round"
                          onClick={() => {
                            setEditing(a.id);
                            setEKind(key || 'follow_up');
                            setEText(a.notes || '');
                          }}
                        >
                          <Icon name="edit" />
                        </button>
                        <button
                          type="button"
                          className="drop"
                          title="Take this round back off"
                          onClick={() => onDropRound(a)}
                        >
                          <Icon name="trash" />
                        </button>
                      </span>
                      {a.notes ? <p>{a.notes}</p> : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="futnone">
                {deal
                  ? 'Nothing logged against the trial yet — the trail above is the lead it came in on.'
                  : 'This account registered directly, so the trail starts with whatever you log here.'}
              </p>
            )}

            {/* Ended, and it says so rather than letting another round be
                written under a decision that has already been made. */}
            {ended ? (
              <p className="futended">
                <Icon name="check" />
                This trial follow-up ended with <b>{OUTCOME[ended][0]}</b>.
              </p>
            ) : (
              <>
                {/*
                  * Another round of chasing. Only the three that leave the
                  * conversation open — ending it is the row of buttons below,
                  * which is a decision and not a dropdown somebody scrolls
                  * past by accident.
                  */}
                <div className="futadd">
                  <select value={pick} onChange={e => setPick(e.target.value)} aria-label="Outcome">
                    {TRIAL_STEPS.map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                  <input
                    value={note}
                    placeholder="What was said"
                    onChange={e => setNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { onRound(pick, note); setNote(''); } }}
                  />
                  <button
                    type="button"
                    className="btn primary sm"
                    onClick={() => { onRound(pick, note); setNote(''); }}
                  >
                    <Icon name="plus" />Add
                  </button>
                </div>

                {/*
                  * And how it ends.
                  *
                  * Plan upgradation is what this screen exists for, so it is a
                  * button rather than the fourth line of a dropdown. Each one
                  * writes a final round and closes the chase — the note in the
                  * box comes with it, so "upgraded to SUITE, annual" is not
                  * retyped.
                  */}
                <div className="futclose">
                  <span>Close it:</span>
                  {TRIAL_ENDS.map(([k, label, tone, icon]) => (
                    <button
                      key={k}
                      type="button"
                      className={`futend ${tone}`}
                      /* An upgrade asks which plan before it closes anything —
                         the plan is the answer this whole screen exists to
                         get, and recording the word without it leaves the
                         report able to say only that somebody upgraded to
                         something. Not interested needs nothing asked. */
                      onClick={() => {
                        if (k === 'plan_upgrade') { onUpgrade(note); return; }
                        onRound(k, note);
                        setNote('');
                      }}
                    >
                      <Icon name={icon} />{label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* The next step, said rather than left to be worked out. */}
          {state === 'following' && !rounds.length && !ended ? (
            <p className="futnext">
              <Icon name="flag" />
              Yours to chase. Ring them, then <b>Log what happened</b> — that
              is what shows here next time, and on their record.
            </p>
          ) : null}

          {/* "Log what happened" went. The outcome box above does the same
              thing in one line and without a sheet, and two doors to one room
              is two places to look for it. */}
          <div className="futdo">
            <button type="button" className="btn" onClick={onOpen}>
              <Icon name="eye" />Open the client
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

export default function FollowUps() {
  const {
    demos, loadDemos, refreshCrm, can, setSheet, toast,
    S, rows, crm, derive, fmtDate, me, patchClient, saveActivity, removeFromCrm
  } = useApp();
  const [openTrial, setOpenTrial] = useState(null);
  /*
   * Two lists, not four states of one.
   *
   * The demos and the trials are different work on different records, and
   * folding "In trial" in as a fourth stat card said they were four cuts of
   * the same thing — so picking it looked like a filter and the three cards
   * beside it went on showing demo counts. A tab strip says what it is: two
   * lists, and you are looking at one of them. The three states stay inside
   * the demos tab, where they are what they always were.
   */
  const [tab, setTab] = useState('demos');
  /* The same three the demos tab has, and the same default: what nobody has
     picked up is the only one of the three that is a queue. Five cuts were
     four more than anybody asked of it. */
  const [tcut, setTcut] = useState('handed');
  const [q, setQ] = useState('');
  const [cut, setCut] = useState('handed');
  const [span, setSpan] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(null);

  const data = demos;
  const loading = data === null;
  const now = Date.now();
  const mayEdit = can('follow-ups', 'edit') || can('demo-bookings', 'edit');

  useEffect(() => { if (demos === null) loadDemos(); }, [demos, loadDemos]);

  /* Narrowing the list folds whatever was open. The panel is taller than
     the row it belongs to, so a filter that changes what is underneath it
     leaves somebody reading one row's detail over another row's list -
     and, when the open row is filtered out, over nothing at all. */
  useEffect(() => { setOpen(null); }, [q, cut, span, from, to]);

  const all = useMemo(() => followUps(data), [data]);

  /* The trials, worked out the same way the Trials menu works them out. */
  const trials = useMemo(() => rows
    .map(r => ({ r, d: derive(r) }))
    .filter(x => x.d.dT !== null && x.d.dT >= 0 && !x.d.excluded)
    .sort((a, b) => a.d.dT - b.d.dT), [rows, derive]);

  /* The lead each trial came in on, where there is one. Built once for the
     whole list: clientOfDeal walks the client rows, and asking it per trial
     inside the render is that walk twenty-seven times over. */
  const dealByClient = useMemo(() => {
    const m = new Map();
    (crm.deals || []).forEach(x => {
      const c = clientOfDeal(rows, x);
      if (c && !m.has(c.id)) m.set(c.id, x);
    });
    return m;
  }, [crm.deals, rows]);

  /*
   * Picking one up.
   *
   * Two fields, both of which the clients app lets us write: whose it is, and
   * that somebody has now spoken to them. That is the whole of "started" —
   * there is no fourth state to store and nowhere honest to store it.
   */
  /*
   * One round of the chase, written where it can be read back.
   *
   * Against the account, not a deal: most trials have no pipeline lead
   * behind them. The subject names the outcome so the list can be rebuilt
   * from the activities alone — no second table, no state to keep in step.
   */
  const addRound = (r, outcome, text) => {
    const label = (ALL_OUTCOMES.find(([k]) => k === outcome) || [])[1] || outcome;
    saveActivity(null, {
      subject: `${ROUND} — ${label}`,
      type: 'Call',
      who: (me && me.name) || S.senderName || '',
      clientId: r.id,
      dealId: '',
      due: new Date().toISOString().slice(0, 10),
      done: true,
      notes: String(text || '').trim()
    });
    patchClient(r.id, { lastContacted: new Date().toISOString().slice(0, 10) });
  };

  /*
   * Which plan, then close.
   *
   * The round, the account and the chase all move together: the note names
   * the plan and the cycle so a report can count them, the client record is
   * updated so the ledger bills the right thing, and the chase ends — which
   * is what the button said it would do.
   */
  const [upgrade, setUpgrade] = useState(null);

  const applyUpgrade = ({ plan, cycle, note: text }) => {
    const r = (rows || []).find(x => String(x.id) === String(upgrade.id));
    setUpgrade(null);
    if (!r) return;
    const said = [`Upgraded to ${plan}`, cycle && `billed ${String(cycle).toLowerCase()}`]
      .filter(Boolean).join(', ');
    addRound(r, 'plan_upgrade', [said, text].filter(Boolean).join(' — '));
    patchClient(r.id, {
      plan,
      cycle,
      /* The trial is over, whatever its end date said. */
      acctType: 'Paying'
    });
    toast(`${r.name || 'This account'} is on ${plan}`);
  };

  /*
   * Correct a round, or take it back off.
   *
   * A trial round is an activity against the account — the subject carries
   * the outcome, which is how it is read back — so correcting one is a save
   * of that activity with a new subject and note. The demo follow-up does
   * the same thing through its own store; this is the trials half of it.
   */
  const fixRound = (a, kind, text) => {
    const label = (ALL_OUTCOMES.find(([k]) => k === kind) || [])[1] || kind;
    saveActivity(a.id, { ...a, subject: `${ROUND} — ${label}`, notes: String(text || '').trim() });
    toast('Round corrected');
  };

  const dropRound = a => {
    if (!window.confirm('Take this round off the follow-up? The note goes with it.')) return;
    removeFromCrm('acts', a.id);
    toast('Round removed');
  };

  const startTrial = r => {
    const mine = r.handler || (me && me.name) || S.senderName || '';
    patchClient(r.id, { handler: mine, lastContacted: new Date().toISOString().slice(0, 10) });
    /*
     * And then something visibly happens.
     *
     * Pressing it used to change a badge and nothing else, which reads as
     * the button having done nothing: the row is unchanged, the list is
     * unchanged, and whatever you were meant to do next is not on screen.
     * It opens the row it started, so the trail, the facts and the two
     * things there are to do are in front of whoever pressed it.
     */
    setOpenTrial(r.id);
    toast(`${r.name || 'This trial'} is yours — log the call when you have made it`);
  };

  const trialsShown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return trials.filter(({ r, d }) => {
      if (needle && ![r.name, r.email, r.contact, r.plan, r.promo, r.handler]
        .join(' ').toLowerCase().includes(needle)) return false;
      const rounds = roundsFor(crm, r.id);
      const closed = rounds.some(a => ENDS.has(outcomeKey(a.outcome)));
      if (tcut === 'done') return closed;
      if (closed) return false;
      if (tcut === 'handed') return trialState(r) === 'handed';
      if (tcut === 'following') return trialState(r) === 'following';
      return true;
    });
  }, [trials, q, tcut, crm]);

  const counts = useMemo(() => {
    const c = { handed: 0, following: 0, closed: 0 };
    all.forEach(b => { c[followState(b)] += 1; });
    return c;
  }, [all]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const today = dubaiToday();
    let lo = null;
    let hi = null;
    if (span === 'custom') {
      lo = from ? Date.parse(`${from}T00:00:00+04:00`) : null;
      hi = to ? Date.parse(`${to}T00:00:00+04:00`) + DAY : null;
    } else if (span === 'month') {
      const d = new Date(today);
      lo = dubaiDayStart(new Date(d.getFullYear(), d.getMonth(), 1));
    } else if (span) {
      lo = today - Number(span) * DAY;
    }

    return all.filter(b => {
      if (b.id === open) return true;
      const s = b.session;

      if (lo !== null || hi !== null) {
        const t = new Date(b.start).getTime();
        if (lo !== null && t < lo) return false;
        if (hi !== null && t >= hi) return false;
      }

      /* Three kinds of cut share one control: the state it is in, what the
         last round came to, and how it ended. */
      if (cut.startsWith('step:')) {
        const last = lastStep(b);
        if (!last || last.kind !== cut.slice(5)) return false;
      } else if (cut.startsWith('end:')) {
        if (followState(b) !== 'closed' || s.closedAs !== cut.slice(4)) return false;
      } else if (followState(b) !== cut) {
        return false;
      }
      if (needle) {
        const who = b.client || b.contact;
        const hay = [
          b.invitee.name, b.invitee.email, b.invitee.phone, s.business, who && who.name, who && who.code,
          s.nextAction, s.outcomeLabel, s.ownerName, s.closedReason, s.handedBy, s.handedNote,
          s.demoNotes, ...(s.feedback || []).map(x => x.text)
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [all, q, cut, span, from, to, open]);

  if (data && data.connected === false) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="flag" tone="g-amber" title="Calendly is not connected">
            Follow-ups come from the demos. Connect Calendly in <code>backend/.env</code> and they appear here.
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  const tiles = [
    { k: 'handed', n: counts.handed, l: 'Not started', i: 'alert', c: 'g-amber', hot: true },
    { k: 'following', n: counts.following, l: 'Following up', i: 'flag', c: 'g-blue' },
    { k: 'closed', n: counts.closed, l: 'Closed', i: 'check', c: 'g-slate' }
  ];

  return (
    <div className="dbk">
      <div className="futabs" role="tablist" aria-label="What to follow up">
        {[['demos', 'Demo follow-ups', counts.handed + counts.following + counts.closed, 'flag'],
          ['trials', 'Trials', trials.length, 'clock']].map(([k, label, n, icon]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={tab === k ? 'on' : undefined}
              onClick={() => setTab(k)}
            >
              <Icon name={icon} />{label}<b>{n}</b>
            </button>
          ))}
      </div>

      {tab === 'demos' ? (
      <div className="stats dbk-stats three">
        {tiles.map(t => (
          <div
            key={t.k}
            className={`stat${cut === t.k ? ' on' : ''}${t.hot && t.n ? ' hot' : ''}`}
            onClick={() => setCut(t.k)}
          >
            <div className={`ic ${t.c}`}><Icon name={t.i} solid /></div>
            <div>
              <div className="n">{loading ? '…' : t.n}</div>
              <div className="l">{t.l}</div>
            </div>
          </div>
        ))}
      </div>
      ) : null}

      <div className="utfilter dbk-filter">
        <div className="f grow">
          <label htmlFor="fu-q">Search</label>
          <input
            id="fu-q"
            value={q}
            placeholder="Name, business, client ID, action, feedback…"
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="f">
          <label htmlFor="fu-when">Demo date</label>
          <select id="fu-when" value={span} onChange={e => setSpan(e.target.value)}>
            {SPANS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        {span === 'custom' ? (
          <>
            <div className="f">
              <label htmlFor="fu-from">From</label>
              <input id="fu-from" type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="f">
              <label htmlFor="fu-to">To</label>
              <input id="fu-to" type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
            </div>
          </>
        ) : null}
        {tab === 'trials' ? (
          <div className="f">
            <label htmlFor="fu-tcut">Show</label>
            <select id="fu-tcut" value={tcut} onChange={e => setTcut(e.target.value)}>
              <option value="handed">Not started</option>
              <option value="following">Following up</option>
              <option value="done">Closed</option>
            </select>
          </div>
        ) : null}
        <div className="f" style={tab === 'trials' ? { display: 'none' } : undefined}>
          <label htmlFor="fu-cut">Show</label>
          <select id="fu-cut" value={cut} onChange={e => setCut(e.target.value)}>
            {WHEN.map(([k, label, group]) => (
              <option key={k || label} value={k} disabled={!!group}>{label}</option>
            ))}
          </select>
        </div>

        {/* One press back to the whole queue. Four controls left in odd
            positions is how a list ends up looking empty when it is not. */}
        <button
          type="button"
          className="rpclear"
          onClick={() => { setQ(''); setSpan(''); setFrom(''); setTo(''); setCut('handed'); setTcut('handed'); }}
          disabled={!q && !span && !from && !to && cut === 'handed' && tcut === 'handed'}
          title="Clear the search and every filter"
        >
          <Icon name="x" />Clear
        </button>
      </div>

      {tab === 'trials' ? (
        trialsShown.length ? (
          <div className="dbk-list fu-list futrials">
            {trialsShown.map(x => (
              <TrialRow
                key={x.r.id}
                r={x.r}
                d={x.d}
                me={me}
                deal={dealByClient.get(x.r.id) || null}
                open={openTrial === x.r.id}
                onToggle={() => setOpenTrial(o => (o === x.r.id ? null : x.r.id))}
                fmtDate={fmtDate}
                rounds={roundsFor(crm, x.r.id)}
                onStart={() => startTrial(x.r)}
                onRound={(outcome, text) => addRound(x.r, outcome, text)}
                onUpgrade={text => setUpgrade({ id: x.r.id, note: text })}
                onFixRound={(a, kind, text) => fixRound(a, kind, text)}
                onDropRound={a => dropRound(a)}
                onLog={() => setSheet({
                  kind: 'activity',
                  dealId: (dealByClient.get(x.r.id) || {}).id,
                  clientId: x.r.id
                })}
                onOpen={() => setSheet({ kind: 'clientView', id: x.r.id })}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardBody>
              <EmptyState icon="clock" tone="g-amber" title={q.trim() ? 'Nothing matches that' : 'Nobody is in a trial'}>
                {q.trim()
                  ? 'Try a different search.'
                  : 'A trial appears here the moment one starts, and leaves it the day it ends.'}
              </EmptyState>
            </CardBody>
          </Card>
        )
      ) : loading ? (
        <p className="muted utload"><Spinner /> Reading the demos…</p>
      ) : !shown.length ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={cut === 'closed' ? 'check' : cut === 'handed' ? 'alert' : 'flag'}
              tone={cut === 'closed' ? 'g-green' : 'g-blue'}
              title={q.trim() ? 'Nothing matches that' : cut === 'handed' ? 'Nothing waiting' : cut === 'following' ? 'Nothing being chased' : 'Nothing closed yet'}
            >
              {q.trim()
                ? 'Try a different search.'
                : cut === 'handed'
                  ? 'A demo appears here when the demo team presses Follow up on the booking and sends it across.'
                  : cut === 'following'
                    ? 'Pick one up from Not started and it moves here.'
                    : 'A follow-up lands here once it is closed with a reason.'}
            </EmptyState>
          </CardBody>
        </Card>
      ) : (
        <div className="dbk-list fu-list">
          {shown.map(b => (
            <FollowRow
              key={b.id}
              b={b}
              data={data}
              now={now}
              open={open === b.id}
              mayEdit={mayEdit}
              /* A row waiting on a Close does not fold away. The panel is
                 the only place that button lives, and folding it left the
                 follow-up on an ending nobody could finish. */
              onToggle={() => {
                if (open === b.id && pendingClose(b)) {
                  toast('Press Close to finish this follow-up first');
                  return;
                }
                setOpen(open === b.id ? null : b.id);
              }}
              onSettled={() => setOpen(null)}
              reload={loadDemos}
              refreshCrm={refreshCrm}
              setSheet={setSheet}
              toast={toast}
            />
          ))}
        </div>
      )}

      <p className="cvnote dbk-note">
        Every row was handed over from a demo booking. Open one to read the
        brief, the queries and everything the demo team took down.
        {/* Two clocks on one row, so the row has to say which is which. */}
        {' '}<b>Demos are {TZ_LABEL}; follow-up rounds are {TZ_WORK_LABEL}.</b>
      </p>

      {/* Which plan, before anything is closed. Held here rather than in the
          global sheet host because it answers back into this page's own
          round-writing rather than into a record by id. */}
      {upgrade ? (
        <PlanUpgradeSheet
          id={upgrade.id}
          onConfirm={applyUpgrade}
          onClose={() => setUpgrade(null)}
        />
      ) : null}
    </div>
  );
}

function FollowRow({
  b, data, now, open, mayEdit, onToggle, onSettled, reload, refreshCrm, setSheet, toast
}) {
  const s = b.session;
  const state = stateOf(b, now);
  const fs = followState(b);
  const due = dueAt(s);
  const [label, tone, icon] = FOLLOW_META[fs] || FOLLOW_META.handed;
  const who = b.client || b.contact;
  const last = (s.feedback || [])[s.feedback.length - 1];

  /* What the row leads with: for one waiting to be picked up, the brief the
     demo team wrote - that is the whole reason it is here. Once sales has
     it, the last thing anybody said about it.

     It reads as somebody's words, so it is set as a quote against a rule in
     its own colour rather than as a second line of grey under a first. */
  const step = last && last.kind ? STEP_META[last.kind] : null;
  const endMeta = fs === 'closed' && s.closedAs ? CLOSED_META[s.closedAs] || CLOSED_META.other : null;

  /* Each of these carries when it happened as well as what was said. A note
     with no date is a note nobody can act on: "not interested" matters very
     differently at two hours old and at three weeks. */
  const note = fs === 'closed'
    ? { tone: 'closed', icon: (endMeta || [0, 0, 'check'])[2], from: (endMeta || ['Closed'])[0], by: last ? last.by || '' : '', at: last ? last.at : null, text: s.closedReason || 'No reason was given' }
    : fs === 'handed'
      ? { tone: 'handed', icon: 'send', from: 'From the demo team', by: s.handedBy || '', at: s.handedAt || null, text: s.handedNote || 'No note was written' }
      /* Once sales has it the row leads with the last round, not with the
         hand-over — that has moved inside, where the whole trail is. */
      : step
        ? { tone: 'live', icon: step[2], from: step[0], by: last.by || '', at: last.at, text: last.text }
        : last
          ? { tone: 'live', icon: 'flag', from: 'Latest note', by: last.by || '', at: last.at, text: last.text }
          : { tone: 'live', icon: 'flag', from: 'Following up', by: s.ownerName || '', at: null, text: 'No round recorded yet' };

  return (
    <article
      id={`fu-${b.id}`}
      className={`dbk-row fu-row ${fs === 'closed' ? 'done' : fs === 'handed' ? 'noshow' : 'upcoming'}${open ? ' open' : ''}${fs === 'handed' ? ' todo' : ''}`}
    >
      <div
        className="dbk-main fu-main"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className="dbk-time">
          {fs === 'handed' ? (
            <>
              <b>{s.handedAt ? dayText(s.handedAt).replace(/ \d{4}$/, '') : '—'}</b>
              <small>handed over</small>
            </>
          ) : (
            <>
              <b>{s.nextDue ? dayText(`${s.nextDue}T12:00:00+04:00`).replace(/ \d{4}$/, '') : '—'}</b>
              <small>{fs === 'closed' ? 'closed' : due ? 'due' : 'no date'}</small>
            </>
          )}
        </div>

        <div className="dbk-who">
          <Avatar name={b.invitee.name || b.invitee.email || '?'} />
          <div>
            <b>
              {b.invitee.name || b.invitee.email || 'Unknown invitee'}
              {s.customerType ? (
                <Pill tone={s.customerType === 'B2B' ? 'gr-indigo' : 'gr-cyan'} className="dbk-client">
                  {s.customerType === 'B2B' ? 'B2B partner' : 'B2C'}
                </Pill>
              ) : null}
              <Pill
                tone={REG_META[REGISTERED(b)][1]}
                icon={REG_META[REGISTERED(b)][2] || undefined}
                className="dbk-client"
              >
                {regLabel(b)}
              </Pill>
              {s.demoCount > 1 ? <Pill tone="gr-violet" className="dbk-client">Demo {s.demoIndex} of {s.demoCount}</Pill> : null}
            </b>
            <small>
              {who && who.code ? <span className="dbk-code">{who.code}</span> : null}
              {/* Booked under another name than the client's - a company
                  name, a colleague - so say whose client this is. */}
              {who && who.name && who.name.trim().toLowerCase() !== String(b.invitee.name || '').trim().toLowerCase()
                ? <span className="fu-client">{who.name} · </span> : null}
              {s.business ? `${s.business} · ` : ''}
              demo {stamp(b.start)} · {state === 'noshow' ? 'no-show' : state === 'canceled' ? 'cancelled' : s.outcomeLabel || 'no outcome'}
            </small>
            {/* Who ran it, carried over from the booking: the first thing a
                sales call asks is "who showed them this". */}
            <Attendees list={s.attendees} />
          </div>
        </div>

        {/* The latest word on this row: what kind of round it was, what was
            said, and by whom and when. The kind sits in a tinted chip so it
            can be told apart at a glance from the words themselves, and the
            who-and-when goes under the quote rather than beside the chip —
            a long name used to push the outcome off its own line. */}
        <div className={`dbk-meet fu-note ${note.tone}`}>
          <span className="fu-from">
            <Icon name={note.icon} />
            {note.from}
          </span>
          <p title={note.text}>{note.text}</p>
          {note.by || note.at ? (
            <span className="fu-when">
              {note.by ? <b>{note.by}</b> : null}
              {note.at ? <time dateTime={note.at} title={`${stamp(note.at)} — ${TZ_LABEL}`}>{workStamp(note.at)}</time> : null}
              {note.at ? <i>{ago(note.at)}</i> : null}
            </span>
          ) : null}
        </div>

        <div className="dbk-state">
          <Pill
            tone={endMeta ? endMeta[1] : tone}
            icon={endMeta ? endMeta[2] : icon}
          >
            {endMeta ? endMeta[0] : label}
          </Pill>
          <small>
            {fs === 'handed'
              ? (s.handedAt ? `sent ${ago(s.handedAt, now)}` : 'waiting on sales')
              : s.ownerName || 'Unassigned'}
          </small>
        </div>

        {/* Taking it on and putting it back are one press, so they stay on
            the row. Closing is not: it wants the round that ended it and
            how, which is what the panel is for. */}
        {mayEdit ? (
          <div className="dbk-go" onClick={e => e.stopPropagation()}>
            {fs === 'handed' ? (
              <StartWork s={s} toast={toast} onDone={reload} />
            ) : fs === 'closed' ? (
              <ReopenButton s={s} toast={toast} onDone={reload} />
            ) : (
              <button type="button" className="btn sm" onClick={onToggle}>
                <Icon name="chev" />Open
              </button>
            )}
          </div>
        ) : null}
      </div>

      {open ? (
        <div className="dbk-detail">
          {/* Who is being called, then the trail of calling them. */}
          <ClientCard b={b} />
          {/* Closing or reopening moves the row to another list, so the
              panel folds rather than staying open over a row that is no
              longer underneath it. */}
          <FollowJourney b={b} mayEdit={mayEdit} reload={reload} toast={toast} onSettled={onSettled} />
          {/* The same record the booking shows: what the form asked, what
              they answered, and every earlier demo they had. */}
          <BookingDetail b={b} data={data} now={now} />
        </div>
      ) : null}
    </article>
  );
}

/** Put a closed follow-up back where it was before it was closed. */
function ReopenButton({ s, onDone, toast }) {
  const [busy, setBusy] = useState(false);
  const { patchDemo } = useApp();
  return (
    <button
      type="button"
      className="btn sm"
      disabled={busy}
      title={s.followPrev === 'handed' ? 'Back to Not started' : 'Back to Following up'}
      onClick={async () => {
        setBusy(true);
        try {
          /* The row leaves the Closed list on the strength of this, so it
             goes into the shared list now rather than after the bookings
             call, which is a minute of Calendly requests away. */
          const r = await api.calendly.update(s.id, { followStatus: s.followPrev || 'following' });
          if (r && r.session) patchDemo(r.session.id, r.session);
          toast(s.followPrev === 'handed' ? 'Back in the queue' : 'Following up again');
          onDone();
        } catch (e) {
          toast(e.message);
          setBusy(false);
        }
      }}
    >
      {busy ? <Spinner /> : <Icon name="undo" />}Reopen
    </button>
  );
}
